import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { seedAllData } from './utils/seedData';

// Public Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingWidget from './components/FloatingWidget';
import Chatbot from './components/Chatbot';
import LoadingScreen from './components/LoadingScreen';
import SocialSidebar from './components/SocialSidebar';

// Public Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Deposits from './pages/Deposits';
import Loans from './pages/Loans';
import FAQ from './pages/FAQ';
import Careers from './pages/Careers';
import Gallery from './pages/Gallery';
import News from './pages/News';
import Contact from './pages/Contact';
import SavingsAccount from './pages/SavingsAccount';
import CurrentAccount from './pages/CurrentAccount';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';


// Scroll to top helper on location change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// App layout wrapper to conditionally display header, footer, widgets
function AppLayout({ children }) {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');

  if (isAdminPath) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100 text-gray-850">
        <main className="flex-grow">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-neutralBg text-navy-light">
      {/* Main Header Menu */}
      <Navbar />

      {/* Dynamic Route Container */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Shared Widgets */}
      <FloatingWidget />
      <Chatbot />
      <SocialSidebar />

      {/* Global Footer */}
      <Footer />
    </div>
  );
}

function MainAppContent() {
  const [appInitializing, setAppInitializing] = useState(true);

  useEffect(() => {
    // Seed Firestore database with mockup data on first load
    seedAllData();

    // Show premium loader for 2 seconds to wow the user
    const timer = setTimeout(() => {
      setAppInitializing(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (appInitializing) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <ScrollToTop />
      <AppLayout>
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/deposits" element={<Deposits />} />
          <Route path="/loans" element={<Loans />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/news" element={<News />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/savings-account" element={<SavingsAccount />} />
          <Route path="/current-account" element={<CurrentAccount />} />

          {/* Admin Routing */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* Fallback Catch-All */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AppLayout>
    </Router>
  );
}

function App() {
  return (
    <MainAppContent />
  );
}

export default App;
